import * as React from "react";
import { useContext, useEffect } from "react";
import { observer } from "mobx-react";
import PageHeader from "../../page-header";
import { StoresContext } from "../../../pages/_app";
import CellBioTable from "./table";
import { CollectionName } from "../../../api";
import UnexpectedBackendError from "../../backend-error";
import { useRouter, useSearchParams } from "next/navigation";
import { reaction } from "mobx";
import { NumberOfSelectedItems } from "../genomes/number-of-selected-label";
import { DownloadSelectedButton } from "../genomes/download-selected-button";
import SearchSuggestionBox from "../../search-suggestion-box";
import { CELL_BIO_SEARCH_SECTIONS } from "../../../utils/search";
import GenomeSearchTags from "../../genome-search-tags";
import { Tab, Tabs } from "@blueprintjs/core";

const CellBioPage = observer(({ collectionName }: { collectionName: CollectionName | null }) => {
  const rootStore = useContext(StoresContext);
  const searchParams = useSearchParams();
  const { error } = rootStore.cellBioStore.listData;
  const router = useRouter();

  useEffect(() => {
    rootStore.cellBioStore.updateIfNotHydrated(collectionName, searchParams);
    const stop = reaction(
      () => rootStore.cellBioStore.urlParams.toString(),
      (urlParams) => {
        let url = "/cell-bio";
        if (collectionName) {
          url = `${url}/${collectionName.replace("_", "-")}`;
        }
        if (urlParams) {
          url = `${url}?${urlParams}`;
        }
        router.push(url);
      }
    );
    return stop;
  }, []);

  const { numTotal, numSelected } = rootStore.cellBioStore.getNumSelected();

  const tabContent = error ? (
    <UnexpectedBackendError />
  ) : (
    <div className="genomes-table-container">
      <div className="genomes-table-container__meta">
        <div className="genomes-table-container__meta__controls">
          <div className="genomes-table-container__download-button">
            <DownloadSelectedButton
              downloadInfos={[
                { type: "exome" },
                { type: "transcriptome" },
                { type: "supplemental_data" },
              ]}
              initBulkSearch={rootStore.cellBioStore.initBulkSearch}
              initBulkDownload={rootStore.cellBioStore.initBulkDownload}
              hasRequiredSubscription={rootStore.userStore.hasActiveCellBioSubscription}
              disabled={numSelected === 0}
            />
          </div>
          <NumberOfSelectedItems
            numSelected={numSelected}
            numTotal={numTotal}
            onSelectAll={rootStore.cellBioStore.selectAll}
            onDeselectAll={rootStore.cellBioStore.deselectAll}
          />
        </div>
        <div className="genomes-table-container__meta__search">
          <GenomeSearchTags
            className="genomes-table-container__meta__search__tags"
            inUseSearchFields={rootStore.cellBioStore.inUseSearchFields}
            removeSearchFilter={rootStore.cellBioStore.removeSearchFilter}
          />
          <div className="genomes-table-container__meta__search__box">
            <SearchSuggestionBox
              performSearch={rootStore.cellBioStore.addSearchFilter}
              searchSections={CELL_BIO_SEARCH_SECTIONS}
            />
          </div>
        </div>
      </div>

      <div className="genomes-table-container__table">
        <CellBioTable collectionName={collectionName} />
      </div>
    </div>
  );

  return (
    <>
      <CellBioHeader />
      <div className="genomes-list">
        <Tabs
          id="genomes-tabs"
          defaultSelectedTabId={
            rootStore.cellBioStore.mineOnly ? "my-cell-lines-tab" : "all-cell-lines-tab"
          }
          renderActiveTabPanelOnly={true}
          animate={false}
          onChange={(name: string) => {
            rootStore.cellBioStore.setMineOnly(name === "my-cell-lines-tab");
          }}
        >
          <Tab title="All Cell Lines" id="all-cell-lines-tab" panel={tabContent} />
          {rootStore.userStore.isLoggedIn() && (
            <Tab title="My Favorite Cell Lines" id="my-cell-lines-tab" panel={tabContent} />
          )}
        </Tabs>
      </div>
    </>
  );
});

const CellBioHeader = observer(() => {
  const rootStore = useContext(StoresContext);
  const { collectionName } = rootStore.cellBioStore;

  return <PageHeader name={pageName(collectionName)} capitalize={true} />;
});

const ANIMAL_CELLS_KEY: CollectionName = "animal_cells";
const HUMAN_CELLS_KEY: CollectionName = "human_cells";
const ORGANOIDS_KEY: CollectionName = "organoids";

const PAGE_NAME_BY_COLLECTION_NAME = {
  [ANIMAL_CELLS_KEY as string]: "Cell Products: Animal Cells",
  [HUMAN_CELLS_KEY as string]: "Cell Products: Human Cells",
  [ORGANOIDS_KEY as string]: "Cell Products: Organoids",
};

function pageName(collectionName: CollectionName | null): string {
  return PAGE_NAME_BY_COLLECTION_NAME[collectionName || ""] ?? "Cell Products";
}

export default CellBioPage;
